// Home Sections Module - Vuex Store
import {
  getHomeSections,
  createHomeSection,
  updateHomeSection,
  deleteHomeSection,
  addProductToSection,
  removeProductFromSection,
  subscribeToHomeSections,
} from '@/services/homeSectionsService'

const state = {
  sections: [],
  loading: false,
  error: null,
  selectedSection: null,
}

const getters = {
  /**
   * Get all sections
   */
  allSections: state => {
    return state.sections
  },

  /**
   * Get active sections sorted by order
   */
  activeSectionsSorted: state => {
    return state.sections
      .filter(section => section.activa !== false)
      .sort((a, b) => (a.orden || 0) - (b.orden || 0))
  },

  /**
   * Get section by ID
   */
  getSectionById: state => id => {
    return state.sections.find(section => section.id === id)
  },

  /**
   * Get loading state
   */
  isLoading: state => {
    return state.loading
  },

  /**
   * Get error state
   */
  getError: state => {
    return state.error
  },

  /**
   * Get selected section
   */
  getSelectedSection: state => {
    return state.selectedSection
  },

  /**
   * Get products count for a section
   */
  getSectionProductsCount: state => sectionId => {
    const section = state.sections.find(s => s.id === sectionId)
    if (!section || !section.productos) {
      return 0
    }
    return Object.keys(section.productos).length
  },
}

const mutations = {
  /**
   * Set sections array
   */
  setSections(state, sections) {
    state.sections = sections || []
  },

  /**
   * Add or update a section in the array
   */
  setSection(state, section) {
    const index = state.sections.findIndex(s => s.id === section.id)
    if (index >= 0) {
      // Update existing section
      state.sections.splice(index, 1, section)
    } else {
      // Add new section
      state.sections.push(section)
    }
    // Sort by order after update
    state.sections.sort((a, b) => (a.orden || 0) - (b.orden || 0))
  },

  /**
   * Remove section from array
   */
  removeSection(state, sectionId) {
    state.sections = state.sections.filter(s => s.id !== sectionId)
  },

  /**
   * Update section products
   */
  updateSectionProducts(state, { sectionId, productos }) {
    const section = state.sections.find(s => s.id === sectionId)
    if (section) {
      section.productos = productos || {}
    }
  },

  /**
   * Add product to section
   */
  addProductToSection(state, { sectionId, product }) {
    const section = state.sections.find(s => s.id === sectionId)
    if (section) {
      if (!section.productos) {
        section.productos = {}
      }
      const productId = product.ProductoID || product.productId || product.id
      if (productId) {
        section.productos[productId] = product
      }
    }
  },

  /**
   * Remove product from section
   */
  removeProductFromSection(state, { sectionId, productId }) {
    const section = state.sections.find(s => s.id === sectionId)
    if (section && section.productos) {
      delete section.productos[productId]
    }
  },

  /**
   * Set loading state
   */
  setLoading(state, loading) {
    state.loading = loading
  },

  /**
   * Set error state
   */
  setError(state, error) {
    state.error = error
  },

  /**
   * Set selected section
   */
  setSelectedSection(state, section) {
    state.selectedSection = section
  },

  /**
   * Clear selected section
   */
  clearSelectedSection(state) {
    state.selectedSection = null
  },
}

const actions = {
  /**
   * Fetch all home sections from Firebase
   */
  async fetchSections({ commit }) {
    commit('setLoading', true)
    commit('setError', null)

    try {
      const result = await getHomeSections()

      if (result.success) {
        commit('setSections', result.data)
        return {
          success: true,
          data: result.data,
        }
      } else {
        commit('setError', result.error || 'Error al obtener secciones')
        commit('setSections', [])
        return {
          success: false,
          error: result.error || 'Error al obtener secciones',
        }
      }
    } catch (error) {
      console.error('Error in fetchSections action:', error)
      commit('setError', error.message || 'Error inesperado al obtener secciones')
      commit('setSections', [])
      return {
        success: false,
        error: error.message || 'Error inesperado al obtener secciones',
      }
    } finally {
      commit('setLoading', false)
    }
  },

  /**
   * Create a new home section
   */
  async createSection({ commit, dispatch }, payload) {
    commit('setLoading', true)
    commit('setError', null)

    try {
      const result = await createHomeSection(payload)

      if (result.success) {
        // Refresh sections to get the new one
        await dispatch('fetchSections')
        return {
          success: true,
          id: result.id,
          data: result.data,
          message: result.message,
        }
      } else {
        commit('setError', result.error || 'Error al crear sección')
        return {
          success: false,
          error: result.error || 'Error al crear sección',
        }
      }
    } catch (error) {
      console.error('Error in createSection action:', error)
      commit('setError', error.message || 'Error inesperado al crear sección')
      return {
        success: false,
        error: error.message || 'Error inesperado al crear sección',
      }
    } finally {
      commit('setLoading', false)
    }
  },

  /**
   * Update a home section
   */
  async updateSection({ commit, dispatch }, { id, payload }) {
    commit('setLoading', true)
    commit('setError', null)

    try {
      const result = await updateHomeSection(id, payload)

      if (result.success) {
        // Refresh sections to get updated data
        await dispatch('fetchSections')
        return {
          success: true,
          message: result.message,
        }
      } else {
        commit('setError', result.error || 'Error al actualizar sección')
        return {
          success: false,
          error: result.error || 'Error al actualizar sección',
        }
      }
    } catch (error) {
      console.error('Error in updateSection action:', error)
      commit('setError', error.message || 'Error inesperado al actualizar sección')
      return {
        success: false,
        error: error.message || 'Error inesperado al actualizar sección',
      }
    } finally {
      commit('setLoading', false)
    }
  },

  /**
   * Delete a home section
   */
  async deleteSection({ commit, dispatch }, id) {
    commit('setLoading', true)
    commit('setError', null)

    try {
      const result = await deleteHomeSection(id)

      if (result.success) {
        // Remove from local state immediately
        commit('removeSection', id)
        // Also refresh to ensure sync
        await dispatch('fetchSections')
        return {
          success: true,
          message: result.message,
        }
      } else {
        commit('setError', result.error || 'Error al eliminar sección')
        return {
          success: false,
          error: result.error || 'Error al eliminar sección',
        }
      }
    } catch (error) {
      console.error('Error in deleteSection action:', error)
      commit('setError', error.message || 'Error inesperado al eliminar sección')
      return {
        success: false,
        error: error.message || 'Error inesperado al eliminar sección',
      }
    } finally {
      commit('setLoading', false)
    }
  },

  /**
   * Add product to a section
   */
  async addProductToSection({ commit, dispatch }, { sectionId, product }) {
    commit('setLoading', true)
    commit('setError', null)

    try {
      const result = await addProductToSection(sectionId, product)

      if (result.success) {
        // Refresh sections to get updated products
        await dispatch('fetchSections')
        return {
          success: true,
          message: result.message,
          productId: result.productId,
        }
      } else {
        commit('setError', result.error || 'Error al agregar producto a la sección')
        return {
          success: false,
          error: result.error || 'Error al agregar producto a la sección',
        }
      }
    } catch (error) {
      console.error('Error in addProductToSection action:', error)
      commit('setError', error.message || 'Error inesperado al agregar producto')
      return {
        success: false,
        error: error.message || 'Error inesperado al agregar producto',
      }
    } finally {
      commit('setLoading', false)
    }
  },

  /**
   * Remove product from a section
   */
  async removeProductFromSection({ commit, dispatch }, { sectionId, productId }) {
    commit('setLoading', true)
    commit('setError', null)

    try {
      const result = await removeProductFromSection(sectionId, productId)

      if (result.success) {
        // Refresh sections to get updated products
        await dispatch('fetchSections')
        return {
          success: true,
          message: result.message,
        }
      } else {
        commit('setError', result.error || 'Error al eliminar producto de la sección')
        return {
          success: false,
          error: result.error || 'Error al eliminar producto de la sección',
        }
      }
    } catch (error) {
      console.error('Error in removeProductFromSection action:', error)
      commit('setError', error.message || 'Error inesperado al eliminar producto')
      return {
        success: false,
        error: error.message || 'Error inesperado al eliminar producto',
      }
    } finally {
      commit('setLoading', false)
    }
  },

  /**
   * Reorder sections (update order for multiple sections)
   */
  async reorderSections({ commit, dispatch }, reorderedList) {
    commit('setLoading', true)
    commit('setError', null)

    try {
      // Update each section's order
      const updatePromises = reorderedList.map((section, index) => {
        const newOrder = index + 1
        if (section.orden !== newOrder) {
          return updateHomeSection(section.id, { orden: newOrder })
        }
        return Promise.resolve({ success: true })
      })

      const results = await Promise.all(updatePromises)

      // Check if all updates succeeded
      const allSuccess = results.every(r => r.success)

      if (allSuccess) {
        // Refresh sections to get updated order
        await dispatch('fetchSections')
        return {
          success: true,
          message: 'Secciones reordenadas correctamente',
        }
      } else {
        const errors = results.filter(r => !r.success).map(r => r.error)
        commit('setError', errors.join(', ') || 'Error al reordenar secciones')
        return {
          success: false,
          error: errors.join(', ') || 'Error al reordenar secciones',
        }
      }
    } catch (error) {
      console.error('Error in reorderSections action:', error)
      commit('setError', error.message || 'Error inesperado al reordenar secciones')
      return {
        success: false,
        error: error.message || 'Error inesperado al reordenar secciones',
      }
    } finally {
      commit('setLoading', false)
    }
  },

  /**
   * Set selected section (for managing products)
   */
  selectSection({ commit, getters }, sectionId) {
    const section = getters.getSectionById(sectionId)
    if (section) {
      commit('setSelectedSection', section)
    }
  },

  /**
   * Clear selected section
   */
  clearSelectedSection({ commit }) {
    commit('clearSelectedSection')
  },

  /**
   * Subscribe to real-time updates
   * Returns unsubscribe function
   */
  subscribeToSections({ commit }) {
    const unsubscribe = subscribeToHomeSections(result => {
      if (result.success) {
        commit('setSections', result.data)
      } else {
        commit('setError', result.error || 'Error en actualización en tiempo real')
      }
    })

    return unsubscribe
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
